import React, { useState, useEffect, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Placeholder from "@/components/Placeholder";
import { toast } from "sonner";
import { Send, Upload, ShieldCheck, Loader2, FileCheck2 } from "lucide-react";

export default function Support() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (user?.email) setForm((f) => ({ ...f, email: user.email, name: f.name || user.email.split("@")[0] }));
    loadUploads();
  }, [user]);

  const loadUploads = () => api.get("/support/action-taker").then((r) => setUploads(r.data)).catch(() => {});

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/support/contact", form);
      toast.success("Message sent. We will get back to you.");
      setForm((f) => ({ ...f, message: "" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/support/action-taker", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Work submitted");
      loadUploads();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid="support-page">
      <div className="eyebrow" style={{ color: "#2038E6" }}>Support</div>
      <h1 className="h-display" style={{ fontSize: "clamp(32px,5vw,48px)", marginTop: 8 }}>We've got you</h1>

      {/* FAQ */}
      <h2 className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 22, marginTop: 28 }}>FAQ</h2>
      <Accordion type="single" collapsible style={{ marginTop: 12 }}>
        {[1, 2, 3, 4].map((i) => (
          <AccordionItem key={i} value={`q${i}`} data-testid={`support-faq-${i}`} style={{ borderColor: "var(--line)" }}>
            <AccordionTrigger className="font-archivo" style={{ fontWeight: 800, textAlign: "left" }}>Question {i}</AccordionTrigger>
            <AccordionContent><Placeholder>FAQ answer from sales page</Placeholder></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Contact form */}
      <h2 className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 22, marginTop: 36 }}>Contact us</h2>
      <form onSubmit={submit} className="card-paper" style={{ padding: 22, marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
        <input data-testid="support-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={inp} />
        <input data-testid="support-email" type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required style={inp} />
        <textarea data-testid="support-message" placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={4} style={{ ...inp, resize: "vertical" }} />
        <button className="btn-cobalt" type="submit" disabled={sending} data-testid="support-submit" style={{ alignSelf: "flex-start" }}>
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send message
        </button>
      </form>

      {/* Guarantee + action-taker upload */}
      <div className="card-paper" style={{ padding: 26, marginTop: 36 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <ShieldCheck size={22} color="#2038E6" />
          <h2 className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 20 }}>Action-taker guarantee</h2>
        </div>
        <p style={{ color: "var(--muted)", marginTop: 10 }}><Placeholder>guarantee terms restated</Placeholder></p>

        <div style={{ marginTop: 18, borderTop: "1px solid var(--line)", paddingTop: 18 }}>
          <div className="eyebrow">Submit your action-taker work</div>
          <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 6 }}>Upload files or screenshots that show you did the work. This is tracked to your account for refund eligibility.</p>
          <input ref={fileRef} type="file" onChange={upload} data-testid="action-taker-input" style={{ display: "none" }} />
          <button className="btn-outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="action-taker-upload" style={{ marginTop: 12 }}>
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload work
          </button>

          {uploads.length > 0 && (
            <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }} data-testid="action-taker-list">
              {uploads.map((u, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--muted)" }}>
                  <FileCheck2 size={16} color="#2038E6" /> {u.filename}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inp = { border: "1px solid var(--line)", borderRadius: 10, padding: "12px 14px", fontFamily: "Inter, sans-serif", fontSize: 15, background: "#fff", width: "100%" };
